import axios from 'axios';
import { authRequestInterceptor } from './authInterceptor';
import { getApiBaseUrl, tenantHeaders } from '../config/apiBase';

export const API_BASE_URL = getApiBaseUrl();
const MEDIA_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

export const getMediaUrl = (path: string): string => {
  if (path.startsWith('http')) return path;
  return `${MEDIA_BASE_URL}${path}`;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    ...tenantHeaders,
  },
});

api.interceptors.request.use(authRequestInterceptor);

export interface Style {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  moves?: Move[];
}

export interface SkillLevel {
  id: string;
  name: string;
  order: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
  moves?: Move[];
}

export interface MoveType {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  moves?: Move[];
}

export interface HandHold {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  moves?: Move[];
}

export interface Position {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  moves?: Move[];
}

export interface Instructor {
  id: string;
  email: string;
  name: string;
  bio?: string;
  profileImage?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  moves?: Move[];
}

export interface MoveVideo {
  id: string;
  moveId: string;
  title?: string;
  videoUrl: string;
  thumbnail?: string;
  duration?: number;
  order: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Move {
  id: string;
  title: string;
  description?: string;
  videoUrl?: string;
  thumbnail?: string;
  duration?: number;
  isPublished: boolean;
  // free | members. `locked`: signed in, but the studio's plans gate this video.
  accessLevel?: 'free' | 'members';
  locked?: boolean;
  isBaseMove: boolean;
  parentMoveId?: string;
  variationDescription?: string;
  createdAt: string;
  updatedAt: string;
  styleId: string;
  skillLevelId: string;
  instructorId: string;
  moveTypeId?: string;
  handHoldId?: string;
  style?: Style;
  skillLevel?: SkillLevel;
  instructor?: Instructor;
  moveType?: MoveType;
  handHold?: HandHold;
  startingHandHold?: HandHold;
  endingHandHold?: HandHold;
  startingPosition?: Position;
  endingPosition?: Position;
  variations?: Move[];
  parentMove?: Move;
  videos?: MoveVideo[];
  demonstrations?: Demonstration[];
  patternMoves?: { id: string; position: number; pattern: Pattern }[];
  _count?: { demonstrations: number };
}

export interface Dancer {
  id: string;
  email: string;
  name: string;
  profileImage?: string;
  bio?: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  demonstrations?: Demonstration[];
}

export type DemonstrationType = 'MOVE' | 'PATTERN';

export interface Demonstration {
  id: string;
  dancerId: string;
  demonstrationType: DemonstrationType;
  moveId: string | null;
  patternId: string | null;
  videoUrl: string;
  thumbnail?: string;
  duration?: number;
  caption?: string;
  recordedAt?: string;
  isFeatured: boolean;
  isApproved: boolean;
  isPublic: boolean;
  feedbackAudience?: string | null;
  feedbackOpen: boolean;
  createdAt: string;
  updatedAt: string;
  dancer?: Dancer;
  move?: Move | null;
  pattern?: Pattern | null;
  feedback?: Feedback[];
}

export interface PatternMove {
  id: string;
  patternId: string;
  moveId: string;
  position: number;
  notes?: string | null;
  move: Move;
}

export interface Pattern {
  id: string;
  title: string;
  slug: string;
  accessLevel?: 'free' | 'members';
  locked?: boolean;
  description?: string | null;
  videoUrl?: string | null;
  thumbnail?: string | null;
  duration?: number | null;
  instructorId?: string | null;
  styleId?: string | null;
  skillLevelId?: string | null;
  classContext?: string | null;
  classDate?: string | null;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
  instructor?: Instructor | null;
  style?: Style | null;
  skillLevel?: SkillLevel | null;
  moves?: PatternMove[];
  _count?: { demonstrations?: number; moves?: number };
}

export interface Feedback {
  id: string;
  demonstrationId: string;
  content: string;
  dancerId?: string;
  instructorId?: string;
  createdAt: string;
  updatedAt: string;
  dancer?: Dancer;
  instructor?: Instructor;
}

export interface DanceRole {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BreakdownAngle {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BreakdownClipType {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MoveBreakdown {
  id: string;
  moveId: string;
  title: string;
  description?: string;
  videoUrl: string;
  thumbnail?: string;
  duration?: number;
  role: DanceRole;
  angle: BreakdownAngle;
  clipType: BreakdownClipType;
  order: number;
  counts?: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

// Public API services for frontend
export const publicApi = {
  // Auth
  auth: {
    me: (): Promise<any> =>
      api.get('/auth/me').then((res) => res.data),
  },

  // Styles
  styles: {
    getAll: (): Promise<Style[]> =>
      api.get('/styles').then((res) => res.data),

    getById: (id: string): Promise<Style> =>
      api.get(`/styles/${id}`).then((res) => res.data),
  },

  // Skill Levels
  skillLevels: {
    getAll: (): Promise<SkillLevel[]> =>
      api.get('/skill-levels').then((res) => res.data),

    getById: (id: string): Promise<SkillLevel> =>
      api.get(`/skill-levels/${id}`).then((res) => res.data),
  },

  // Instructors
  instructors: {
    getAll: (): Promise<Instructor[]> =>
      api.get('/instructors').then((res) => res.data),

    getById: (id: string): Promise<Instructor> =>
      api.get(`/instructors/${id}`).then((res) => res.data),
  },

  // Move Types
  moveTypes: {
    getAll: (): Promise<MoveType[]> =>
      api.get('/move-types').then((res) => res.data),

    getById: (id: string): Promise<MoveType> =>
      api.get(`/move-types/${id}`).then((res) => res.data),
  },

  // Hand Holds
  handHolds: {
    getAll: (): Promise<HandHold[]> =>
      api.get('/hand-holds').then((res) => res.data),

    getById: (id: string): Promise<HandHold> =>
      api.get(`/hand-holds/${id}`).then((res) => res.data),
  },

  // Positions
  positions: {
    getAll: (): Promise<Position[]> =>
      api.get('/positions').then((res) => res.data),

    getById: (id: string): Promise<Position> =>
      api.get(`/positions/${id}`).then((res) => res.data),
  },

  // Moves with filtering
  moves: {
    getAll: (filters?: {
      styleId?: string;
      skillLevelId?: string;
      instructorId?: string;
      moveTypeId?: string;
      handHoldId?: string;
      includeVariations?: boolean;
    }): Promise<Move[]> =>
      api.get('/moves', {
        params: {
          ...filters,
          includeVariations: filters?.includeVariations ? 'true' : 'false'
        }
      }).then((res) => res.data),

    getById: (id: string): Promise<Move> =>
      api.get(`/moves/${id}`).then((res) => res.data),

    create: (data: {
      title: string;
      description?: string;
      videoUrl: string;
      thumbnail?: string;
      duration?: number;
      styleId: string;
      skillLevelId: string;
      instructorId: string;
      isPublished?: boolean;
      isBaseMove?: boolean;
      parentMoveId?: string;
      variationDescription?: string;
    }): Promise<Move> =>
      api.post('/moves', data).then((res) => res.data),
  },

  // Dancers
  dancers: {
    getAll: (includeUnverified = false): Promise<Dancer[]> =>
      api.get('/dancers', { params: { includeUnverified } }).then((res) => res.data),

    getById: (id: string): Promise<Dancer> =>
      api.get(`/dancers/${id}`).then((res) => res.data),

    register: (data: { email: string; name: string; bio?: string; profileImage?: string }): Promise<Dancer> =>
      api.post('/dancers/register', data).then((res) => res.data),

    update: (id: string, data: { name?: string; bio?: string; profileImage?: string }): Promise<Dancer> =>
      api.put(`/dancers/${id}`, data).then((res) => res.data),

    verify: (id: string): Promise<Dancer> =>
      api.post(`/dancers/${id}/verify`).then((res) => res.data),

    unverify: (id: string): Promise<Dancer> =>
      api.post(`/dancers/${id}/unverify`).then((res) => res.data),

    delete: (id: string): Promise<void> =>
      api.delete(`/dancers/${id}`).then(() => undefined),
  },

  // Demonstrations
  demonstrations: {
    getAll: (filters?: { moveId?: string; dancerId?: string; featured?: boolean }): Promise<Demonstration[]> =>
      api.get('/demonstrations', { params: filters }).then((res) => res.data),

    getFeatured: (): Promise<Demonstration | null> =>
      api.get('/demonstrations/featured').then((res) => res.data),

    /** Instructor review queue — oldest first; awaitingReply = no instructor answer yet. */
    getQueue: (): Promise<(Demonstration & { awaitingReply: boolean })[]> =>
      api.get('/demonstrations/queue').then((res) => res.data),

    getByMove: (moveId: string): Promise<Demonstration[]> =>
      api.get(`/demonstrations/move/${moveId}`).then((res) => res.data),

    getByPattern: (patternId: string): Promise<Demonstration[]> =>
      api.get(`/demonstrations/pattern/${patternId}`).then((res) => res.data),

    getById: (id: string): Promise<Demonstration> =>
      api.get(`/demonstrations/${id}`).then((res) => res.data),

    upload: (data: FormData): Promise<Demonstration> =>
      api.post('/demonstrations/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then((res) => res.data),

    update: (id: string, data: { caption?: string; isPublic?: boolean; feedbackAudience?: string | null; feedbackOpen?: boolean }): Promise<Demonstration> =>
      api.put(`/demonstrations/${id}`, data).then((res) => res.data),

    requestFeedback: (id: string, audience: 'instructors' | 'all'): Promise<Demonstration> =>
      api.post(`/demonstrations/${id}/request-feedback`, { audience }).then((res) => res.data),

    closeFeedback: (id: string): Promise<Demonstration> =>
      api.post(`/demonstrations/${id}/close-feedback`).then((res) => res.data),

    approve: (id: string): Promise<Demonstration> =>
      api.post(`/demonstrations/${id}/approve`).then((res) => res.data),

    reject: (id: string): Promise<Demonstration> =>
      api.post(`/demonstrations/${id}/reject`).then((res) => res.data),

    feature: (id: string): Promise<Demonstration> =>
      api.post(`/demonstrations/${id}/feature`).then((res) => res.data),

    unfeature: (id: string): Promise<Demonstration> =>
      api.post(`/demonstrations/${id}/unfeature`).then((res) => res.data),

    getMine: (): Promise<Demonstration[]> =>
      api.get('/demonstrations/mine').then((res) => res.data),

    delete: (id: string): Promise<void> =>
      api.delete(`/demonstrations/${id}`).then(() => undefined),
  },

  // Dance Roles
  danceRoles: {
    getAll: (): Promise<DanceRole[]> =>
      api.get('/dance-roles').then((res) => res.data),
  },

  // Breakdown Angles
  breakdownAngles: {
    getAll: (): Promise<BreakdownAngle[]> =>
      api.get('/breakdown-angles').then((res) => res.data),
  },

  // Breakdown Clip Types
  breakdownClipTypes: {
    getAll: (): Promise<BreakdownClipType[]> =>
      api.get('/breakdown-clip-types').then((res) => res.data),
  },

  // Patterns
  patterns: {
    getAll: (filters?: {
      styleId?: string;
      skillLevelId?: string;
      instructorId?: string;
      search?: string;
    }): Promise<Pattern[]> =>
      api.get('/patterns', { params: filters }).then((res) => res.data),

    getById: (idOrSlug: string): Promise<Pattern> =>
      api.get(`/patterns/${idOrSlug}`).then((res) => res.data),
  },

  // Breakdowns
  breakdowns: {
    getByMove: (moveId: string, role?: string): Promise<MoveBreakdown[]> =>
      api.get(`/breakdowns/move/${moveId}`, { params: role ? { role } : {} }).then((res) => res.data),

    getById: (id: string): Promise<MoveBreakdown> =>
      api.get(`/breakdowns/${id}`).then((res) => res.data),
  },

  // Feedback
  feedback: {
    getByDemonstration: (demonstrationId: string): Promise<Feedback[]> =>
      api.get(`/feedback/demonstration/${demonstrationId}`).then((res) => res.data),

    addFromDancer: (data: { demonstrationId: string; content: string }): Promise<Feedback> =>
      api.post('/feedback/dancer', data).then((res) => res.data),

    addFromInstructor: (data: { demonstrationId: string; content: string }): Promise<Feedback> =>
      api.post('/feedback/instructor', data).then((res) => res.data),

    update: (id: string, data: { content: string }): Promise<Feedback> =>
      api.put(`/feedback/${id}`, data).then((res) => res.data),

    delete: (id: string): Promise<void> =>
      api.delete(`/feedback/${id}`).then(() => undefined),
  },
};

export type LiveStreamStatus = 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';

export interface LiveStream {
  id: number;
  instructorId: number;
  title: string;
  description?: string | null;
  visibility: 'PUBLIC' | 'PRIVATE';
  scheduledStart: string;
  estimatedDurationMins?: number | null;
  status: LiveStreamStatus;
  startedAt?: string | null;
  endedAt?: string | null;
  cfRecordingUid?: string | null;
  recordingDurationSecs?: number | null;
  createdAt: string;
  updatedAt: string;
  instructor?: { id: number; name: string; profileImage?: string | null };
}

export interface LiveStreamPlayback {
  mode: 'live' | 'replay';
  hlsUrl: string;
  expiresAt: string;
}

export interface GoLiveResponse {
  id: number;
  status: LiveStreamStatus;
  whipUrl: string;
  rtmps?: { url: string; streamKey: string };
}

export interface LiveStreamInput {
  title: string;
  description?: string | null;
  scheduledStart: string;
  estimatedDurationMins?: number | null;
}

export type LiveStreamFilter = 'upcoming' | 'live' | 'replays' | 'mine';

export const liveStreamApi = {
  list: (filter?: LiveStreamFilter): Promise<LiveStream[]> =>
    api.get('/live-streams', { params: filter ? { filter } : {} }).then((res) => res.data),

  get: (id: number): Promise<LiveStream> =>
    api.get(`/live-streams/${id}`).then((res) => res.data),

  create: (data: LiveStreamInput): Promise<LiveStream> =>
    api.post('/live-streams', data).then((res) => res.data),

  update: (id: number, data: Partial<LiveStreamInput>): Promise<LiveStream> =>
    api.patch(`/live-streams/${id}`, data).then((res) => res.data),

  cancel: (id: number): Promise<LiveStream> =>
    api.post(`/live-streams/${id}/cancel`).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/live-streams/${id}`).then(() => undefined),

  goLive: (id: number): Promise<GoLiveResponse> =>
    api.post(`/live-streams/${id}/go-live`).then((res) => res.data),

  end: (id: number): Promise<LiveStream> =>
    api.post(`/live-streams/${id}/end`).then((res) => res.data),

  getPlayback: (id: number): Promise<LiveStreamPlayback> =>
    api.get(`/live-streams/${id}/playback`).then((res) => res.data),
};

export interface MeResponse {
  id: number;
  cognitoSub: string;
  email: string;
  name: string;
  tenantId: string;
  role: 'owner' | 'instructor' | 'student';
  dancer: { id: number; name: string; email: string; isActive: boolean } | null;
  instructor: { id: number; name: string; email: string; isActive: boolean } | null;
  createdAt: string;
  updatedAt: string;
}

export const authApi = {
  me: (): Promise<MeResponse> => api.get('/auth/me').then((res) => res.data),
  redeemInvite: (code: string): Promise<MeResponse> =>
    api.post('/auth/redeem-invite', { code }).then((res) => res.data),
};

// Paid memberships: the studio's plans, the viewer's access, checkout, portal
export interface MembershipPlan {
  id: number;
  name: string;
  description: string | null;
  priceCents: number;
  interval: 'month' | 'year';
  includesLibrary: boolean;
  includesFeedback: boolean;
  includesLive: boolean;
}

export interface PlansResponse {
  membershipsEnabled: boolean;
  canJoin: boolean;
  currency?: string;
  plans: MembershipPlan[];
}

export type Perk = 'library' | 'feedback' | 'live';

export interface MyMembership {
  membershipsEnabled: boolean;
  isStaff: boolean;
  perks: Record<Perk, boolean>;
  subscriptions: {
    id: number;
    source: 'stripe' | 'manual';
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    expiresAt: string | null;
    plan: MembershipPlan;
  }[];
  canManageBilling: boolean;
}

export const billingApi = {
  getPlans: (): Promise<PlansResponse> => api.get('/billing/plans').then((res) => res.data),
  getMine: (): Promise<MyMembership> => api.get('/billing/me').then((res) => res.data),
  checkout: (planId: number): Promise<{ url: string }> =>
    api.post('/billing/checkout', { planId }).then((res) => res.data),
  portal: (): Promise<{ url: string }> => api.post('/billing/portal').then((res) => res.data),
};

/** True when an API error means "your plan doesn't include this". */
export function isPlanRequired(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { error?: string } } };
  return e?.response?.status === 403 && e.response.data?.error === 'plan_required';
}

export const formatPrice = (cents: number, currency = 'usd') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

export default api;